import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { withField, IFieldComponentMeta, IMessageValues } from 'react-ocean-forms';
import { AsyncPageableSelectProps, MessageCallbackObj, Message, GetMessageCallback } from './AsyncPageableSelect.types';
import { ISelectOption, IPreparedSelectProps, FieldLine, SelectBase, isSelectOption } from 'react-ocean-forms-bootstrap';
import AsyncPaginate, { AsyncResult } from 'react-select-async-paginate';
import { Badge, Input } from 'reactstrap';

function getMessageCallback(meta: IFieldComponentMeta, messageCallbackObj: MessageCallbackObj, message?: Message): string | null {
  if (typeof message === 'function') {
    return message(messageCallbackObj);
  } else if (typeof message === 'string') {
    return meta.stringFormatter(message, (messageCallbackObj as unknown) as IMessageValues);
  }

  return null;
}

const defaultProps = {
  placeholder: 'ojs_select_placeholder',
  loadingMessage: 'ojs_select_loading_message',
  noOptionsMessage: 'ojs_select_no_options_message',
};

/**
 * 
 * @param props 
 */
export const BaseAsyncPageableSelect: React.FC<AsyncPageableSelectProps> = (props: Readonly<AsyncPageableSelectProps>): JSX.Element => {
  const { handleChange, ...selectProps } = props;
  const { meta, additional,  noOptionsMessage, loadingMessage, loadOptions, valueControlled,  ...selectBaseProps} = selectProps;

  const baseProps = {
    ...defaultProps,
    ...selectBaseProps,
    meta,
  };
  const firstUpdate = useRef(true);
  
  
  useLayoutEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
      props.field.onChange({ 
        target: {
          value: valueControlled as any,
        }
      });
  // props.field is not listed as dependency because withField is not stable 
  },[valueControlled]);
 

  const noOptionsMessageCb = useCallback<GetMessageCallback>((v: MessageCallbackObj) => getMessageCallback(meta, v, noOptionsMessage), [
    meta,
    noOptionsMessage,
  ]);

  /**
   * Callback that returns the text that should be shown while the
   * asynchronous data is loaded
   */
  const loadingMessageCb = useCallback<GetMessageCallback>((v: MessageCallbackObj) => getMessageCallback(meta, v, loadingMessage), [meta, loadingMessage]);

  const styles = useMemo(() => { 
    return {
      multiValue: (base, state) => {
        return state.data.isFixed ? { ...base, backgroundColor: 'gray' } : base;
      },
      multiValueLabel: (base, state) => {
        return state.data.isFixed
          ? { ...base, fontWeight: 'bold', color: 'white', paddingRight: 6 }
          : base;
      },
      multiValueRemove: (base, state) => {
        return state.data.isFixed ? { ...base, display: 'none' } : base;
      },
    }
  },[]);

  const loadOptionsCallback = useCallback(
    async(inputValue: string, prevOptions: ISelectOption[], additional: unknown): Promise<AsyncResult<ISelectOption, unknown>> => {
      const result = await loadOptions(inputValue, prevOptions, additional);
      return {
        ...result,
      };
    },
    [loadOptions]
  );


  const renderSelect = useCallback((preparedProps: IPreparedSelectProps): JSX.Element => {
    const { field } = props;
    let valueSelect;
    
    // override with controlled value
    if(valueControlled === null || valueControlled) {
      valueSelect = valueControlled;
    }
    // override with field value if undefined
    if(!valueSelect) {
      valueSelect = preparedProps.value ? preparedProps.value : null;
    }
    // console.log('SELECT', valueSelect, preparedProps.value);
  
    return (
      <AsyncPaginate
        {...preparedProps}
        styles={styles}
        key={preparedProps.id}
        classNamePrefix="ocean"
        debounceTimeout={400}
        noOptionsMessage={noOptionsMessageCb}
        loadingMessage={loadingMessageCb}
        value={valueSelect}
        loadOptions={loadOptionsCallback}
        onChange={(value, { action, removedValue }) => {
          
          switch(action) {
            case 'remove-value':
            case 'pop-value':
              if (removedValue && removedValue.isFixed) {
                return
              }
              break;
            case 'clear':
              // @ts-ignore
              value = Array.isArray(field.value) ? field.value.filter(v => v.isFixed) : value;
              break;
          }
          field.onChange({ 
            target: {
              value: value,
            }
          });
          if (handleChange) handleChange(value, { action });
          
        }}
        additional={additional}
      />
    )
  },[additional, props, handleChange, loadOptionsCallback, loadingMessageCb, styles, noOptionsMessageCb,valueControlled ]);

  if (meta.plaintext) {
    return(
      <FieldLine label={baseProps.label} field={baseProps.field} meta={baseProps.meta} >
        { isSelectOption(baseProps.field.value) &&
           <Input {...baseProps.field}  plaintext readOnly  value={baseProps.field.value.label} />
        }
        <div className="mt-2">
        {(Array.isArray(baseProps.field.value)) &&
          baseProps.field.value.map(v => {
             return (
                <Badge 
                  data-toggle="tooltip" 
                  className="text-truncate mr-2" 
                  style={{fontSize: "100%", whiteSpace: "unset", textAlign: "inherit", fontWeight: "inherit"}}>
                    {v.label}
                </Badge>
             );
              
          })
        }
        </div>
    
      </FieldLine>
    );
  }


  return (
    <SelectBase {...baseProps} 
      clearable={valueControlled ? false : baseProps.clearable}
      options={[]} 
      handleChange={handleChange} 
      renderSelect={renderSelect} />
  );
}

BaseAsyncPageableSelect.displayName = 'AsyncPageableSelect';

export const AsyncPageableSelect = withField(BaseAsyncPageableSelect);
